// Manual OAuth PKCE implementation for Keycloak
// Replaces keycloak-js entirely for compatibility with Keycloak v26

class KeycloakAuthService {
  appSettings;
  token = null;
  account = false;
  authState = null;
  codeVerifier = null;

  constructor(appSettings) {
    if (!appSettings) {
      throw new Error("the app settings service was not provided");
    }
    this.appSettings = appSettings;
  }

  get baseUrl() {
    return this.appSettings.GetKeyCloakServerURL();
  }

  get realm() {
    return this.appSettings.GetKeyCloakRealm();
  }

  get clientId() {
    return this.appSettings.GetKeyCloakClientId();
  }

  get redirectUri() {
    return this.appSettings.GetLoginRedirectUri();
  }

  get logoutRedirectUri() {
    return this.appSettings.GetLogoutRedirectUri();
  }

  // Generate a random string for PKCE
  _randomString(length) {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => chars[byte % chars.length]).join("");
  }

  // SHA-256 hash then base64url encode for PKCE challenge
  async _sha256(plain) {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return btoa(String.fromCharCode(...new Uint8Array(hash)))
      .replace(/=/g, "")
      .replace(/\+/g, "-")
      .replace(/\//g, "_");
  }

  _getTokenEndpoint() {
    return this.baseUrl + "/realms/" + this.realm + "/protocol/openid-connect/token";
  }

  _getAuthEndpoint() {
    return this.baseUrl + "/realms/" + this.realm + "/protocol/openid-connect/auth";
  }

  _getLogoutEndpoint() {
    return this.baseUrl + "/realms/" + this.realm + "/protocol/openid-connect/logout";
  }

  // Check URL for OAuth callback params and exchange code for token
  async HandlePageLoadEvent() {
    const hash = window.location.hash;

    // Check if this is a callback from Keycloak (has code + state)
    if (hash && hash.includes("code=") && hash.includes("state=")) {
      console.log("[PKCE] Detected OAuth callback, exchanging code for token");

      const params = new URLSearchParams(hash.substring(1));
      const code = params.get("code");
      const returnedState = params.get("state");
      const savedState = sessionStorage.getItem("pkce_auth_state");
      const savedVerifier = sessionStorage.getItem("pkce_code_verifier");

      // Clean URL immediately
      window.history.replaceState({}, document.title, window.location.pathname);

      // Verify state matches (CSRF protection)
      if (returnedState !== savedState) {
        console.error("[PKCE] State mismatch — possible CSRF attack");
        throw new Error("State verification failed");
      }

      if (!code || !savedVerifier) {
        throw new Error("Missing code or code verifier");
      }

      // Exchange authorization code for tokens
      const body = new URLSearchParams({
        grant_type: "authorization_code",
        code: code,
        redirect_uri: this.redirectUri,
        client_id: this.clientId,
        code_verifier: savedVerifier,
      });

      const response = await fetch(this._getTokenEndpoint(), {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error("Token exchange failed: " + response.status + " " + errText);
      }

      const tokenData = await response.json();
      this.token = tokenData.access_token;
      this.account = true;
      sessionStorage.setItem("pkce_access_token", tokenData.access_token);
      console.log("[PKCE] Authentication successful");

      // Clean up stored state
      sessionStorage.removeItem("pkce_auth_state");
      sessionStorage.removeItem("pkce_code_verifier");
      sessionStorage.removeItem("pkce_nonce");

      return;
    }

    // No callback params - try to restore token from sessionStorage
    const savedToken = sessionStorage.getItem("pkce_access_token");
    if (savedToken) {
      this.token = savedToken;
      this.account = true;
      console.log("[PKCE] Restored token from sessionStorage");
      return;
    }
    console.log("[PKCE] No OAuth callback, user not authenticated");
    this.account = false;
    this.token = null;
  }

  // Redirect to Keycloak login with PKCE
  SignIn() {
    const state = this._randomString(32);
    const nonce = this._randomString(32);
    const codeVerifier = this._randomString(96);

    // Don't clear existing token - GetToken() now recovers from sessionStorage first
    // sessionStorage.removeItem("pkce_access_token");

    // Store for callback verification
    sessionStorage.setItem("pkce_auth_state", state);
    sessionStorage.setItem("pkce_code_verifier", codeVerifier);
    sessionStorage.setItem("pkce_nonce", nonce);

    // Generate PKCE challenge
    this._sha256(codeVerifier).then((codeChallenge) => {
      const authUrl =
        this._getAuthEndpoint() + "?" +
        "client_id=" + encodeURIComponent(this.clientId) +
        "&redirect_uri=" + encodeURIComponent(this.redirectUri) +
        "&state=" + encodeURIComponent(state) +
        "&nonce=" + encodeURIComponent(nonce) +
        "&response_type=code" +
        "&response_mode=fragment" +
        "&scope=" + encodeURIComponent("openid profile email") +
        "&code_challenge=" + codeChallenge +
        "&code_challenge_method=S256";

      window.location.replace(authUrl);
    });
  }

  // Get token for API calls - try sessionStorage first before redirecting
  async GetToken() {
    // Try to recover from sessionStorage if we have it but memory doesn't
    if (!this.account && typeof sessionStorage !== 'undefined') {
      const saved = sessionStorage.getItem("pkce_access_token");
      if (saved) {
        this.token = saved;
        this.account = true;
        console.log("[PKCE] GetToken recovered from sessionStorage");
      }
    }

    if (!this.account || !this.token) {
      console.log("[PKCE] GetToken called but user not authenticated");
      return null;
    }
    return { accessToken: this.token };
  }

  // Logout
  SignOut() {
    const logoutUrl =
      this._getLogoutEndpoint() + "?" +
      "redirect_uri=" + encodeURIComponent(this.logoutRedirectUri);

    this.token = null;
    this.account = false;
    sessionStorage.removeItem("pkce_access_token");
    window.location.replace(logoutUrl);
  }
}

export default KeycloakAuthService;
